/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import saveAs from 'file-saver';
import { toast } from 'react-hot-toast';
import { useLocalStorage } from 'react-use';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { withAsyncPaginate } from 'react-select-async-paginate';
import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import {
  CustomPaging,
  DataTypeProvider,
  EditingState,
  IntegratedSelection,
  IntegratedSorting,
  PagingState,
  SearchState,
  SortingState,
  SelectionState
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  ColumnChooser,
  ExportPanel,
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  TableEditColumn,
  TableEditRow,
  TableHeaderRow,
  TableInlineCellEditing,
  TableSelection,
  Toolbar,
  DragDropProvider
} from '@devexpress/dx-react-grid-material-ui';
import { Chip, CircularProgress, IconButton, MenuItem, Paper, Select, TextField, Tooltip } from '@mui/material';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InvoiceDialogContent from '../InvoiceDialogContent';
import DueDateInput from '../DueDateInput';
import AutoSaveInlineCell from '../../common/AutoSaveInlineCell';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';
import HeaderFilterCell, { HeaderFilterContext, getRowCellValue } from './customTemplate/HeaderFilterCell';
import {
  boxshallProductOption,
  calculateLineTotal,
  deletesWholeInvoice,
  dueDateFromVendorTerm,
  emptyLineItem,
  isLineAmountValid,
  rowToPayload,
  rowToPayloadAfterLineItemDeletion,
  toGridRows
} from '../helper';
import CustomToolbarRoot, { BULK_ACTIONS_BY_STATUS } from './customTemplate/CustomToolbarRoot';
import KebabCommandCell from './customTemplate/KebabCommandCell';
import InvoiceDialog from './dialog/InvoiceDialog';
import CreateBoxshallProductDialog from '../CreateBoxshallProductDialog';
import CreateCustomerDialog from '../CreateCustomerDialog';
import CreateVendorDialog from '../CreateVendorDialog';

const AsyncPaginateCreatable = withAsyncPaginate(CreatableSelect);

const INLINE_COLUMNS = [
  'supplierName',
  'invoiceNumber',
  'invoiceDate',
  'dueDate',
  'productService',
  'boxshallName',
  'boxshallDescription',
  'productServiceDescription',
  'quantity',
  'productServiceRate',
  'amount',
  'billableStatus',
  'taxCode',
  'markupPercent',
  'customerRef',
  'classRef'
];

const REQUIRED_NEW_ROW_COLUMNS = [
  'supplierName',
  'dueDate',
  'invoiceDate',
  'quantity',
  'boxshallName',
  'boxshallDescription',
  'invoiceNumber',
  'productServiceRate'
];

const isEmptyRequiredValue = (value) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

// eslint-disable-next-line no-undef
const IMMEDIATE_COMMIT_COLUMNS = new Set([
  'supplierName',
  'boxshallName',
  'billableStatus',
  'taxCode',
  'customerRef',
  'classRef'
]);

const InvoiceAutoSaveCell = (props) => (
  <AutoSaveInlineCell {...props} immediateCommitColumns={IMMEDIATE_COMMIT_COLUMNS} />
);

const NewInvoiceEditCell = ({ column, value, ...props }) => {
  const requiredValueMissing = REQUIRED_NEW_ROW_COLUMNS.includes(column.name) && isEmptyRequiredValue(value);
  const lineAmountInvalid =
    column.name === 'amount' && !isLineAmountValid(props.row?.quantity, props.row?.productServiceRate, value);
  return (
    <TableEditRow.Cell
      {...props}
      column={column}
      value={value}
      title={
        requiredValueMissing
          ? `${column.title} is required`
          : lineAmountInvalid
          ? 'Amount must equal Quantity × Product Service Rate excluding GST'
          : props.title
      }
      style={{
        ...props.style,
        ...((requiredValueMissing || lineAmountInvalid) && { boxShadow: 'inset 0 0 0 1px #d32f2f' })
      }}
    />
  );
};

const COLUMNS = [
  { name: 'supplierName', title: 'Supplier' },
  { name: 'invoiceNumber', title: 'Bill Number' },
  { name: 'invoiceDate', title: 'Bill Date' },
  { name: 'dueDate', title: 'Due Date' },
  { name: 'productService', title: 'Product Service' },
  { name: 'boxshallName', title: 'Boxshall Name' },
  { name: 'boxshallDescription', title: 'Boxshall Description' },
  { name: 'productServiceDescription', title: 'Product Service Description' },
  { name: 'quantity', title: 'Quantity' },
  { name: 'productServiceRate', title: 'Product Service Rate' },
  { name: 'amount', title: 'Amount' },
  { name: 'calculatedTotal', title: 'Total' },
  { name: 'billableStatus', title: 'Product Service Billable Status' },
  { name: 'taxCode', title: 'Product Service Tax Code' },
  { name: 'markupPercent', title: 'Product Service Markup Percent' },
  { name: 'customerRef', title: 'Billable Customer: Product Service' },
  { name: 'classRef', title: 'Product Service Class' },
  { name: 'extractionStatus', title: 'Extraction Status' },
  { name: 'approvalStatus', title: 'Approval Status' },
  { name: 'receivedAt', title: 'Received' },
  { name: 'actions', title: 'Actions' }
];

const IMPORT_COLUMN_NAMES = [
  'supplierName',
  'dueDate',
  'invoiceDate',
  'invoiceNumber',
  'boxshallName',
  'boxshallDescription',
  'actions',
  'productService',
  'productServiceDescription',
  'quantity',
  'productServiceRate',
  'amount',
  'calculatedTotal',
  'billableStatus',
  'taxCode',
  'receivedAt',
  'markupPercent',
  'customerRef',
  'classRef'
];
const IMPORT_COLUMNS = IMPORT_COLUMN_NAMES.map((name) => COLUMNS.find((column) => column.name === name));

const DEFAULT_WIDTHS = [
  { columnName: 'supplierName', width: 190 },
  { columnName: 'invoiceNumber', width: 145 },
  { columnName: 'invoiceDate', width: 130 },
  { columnName: 'dueDate', width: 130 },
  { columnName: 'productService', width: 150 },
  { columnName: 'boxshallName', width: 180 },
  { columnName: 'boxshallDescription', width: 230 },
  { columnName: 'productServiceDescription', width: 260 },
  { columnName: 'quantity', width: 105 },
  { columnName: 'productServiceRate', width: 165 },
  { columnName: 'amount', width: 165 },
  { columnName: 'calculatedTotal', width: 165 },
  { columnName: 'billableStatus', width: 210 },
  { columnName: 'taxCode', width: 180 },
  { columnName: 'markupPercent', width: 220 },
  { columnName: 'customerRef', width: 260 },
  { columnName: 'classRef', width: 210 },
  { columnName: 'extractionStatus', width: 160 },
  { columnName: 'approvalStatus', width: 155 },
  { columnName: 'receivedAt', width: 175 },
  { columnName: 'actions', width: 110 }
];

const getRowId = (row) => row.id;

const formatDate = (value, withTime = false) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return format(date, withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy');
};

const DateFormatter = ({ value, column }) => <span>{formatDate(value, column.name === 'receivedAt')}</span>;
const DateEditor = ({ value, onValueChange, column, autoFocus, onBlur, onKeyDown, disabled }) => (
  <TextField
    autoFocus={autoFocus}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    type="date"
    size="small"
    variant="standard"
    name={column.name}
    value={value || ''}
    onChange={(event) => onValueChange(event.target.value)}
    disabled={disabled}
    fullWidth
  />
);
const DateProvider = (props) => (
  <DataTypeProvider {...props} formatterComponent={DateFormatter} editorComponent={DateEditor} />
);
const DueDateEditor = ({ value, row, onValueChange, autoFocus, onBlur, onKeyDown, disabled, terms, loading }) => (
  <DueDateInput
    invoiceDate={row.invoiceDate}
    value={value || ''}
    onChange={onValueChange}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    autoFocus={autoFocus}
    disabled={disabled}
    terms={terms}
    loading={loading}
    variant="standard"
  />
);
const DueDateProvider = ({ terms, loading, ...props }) => (
  <DataTypeProvider
    {...props}
    formatterComponent={DateFormatter}
    editorComponent={(editorProps) => <DueDateEditor {...editorProps} terms={terms} loading={loading} />}
  />
);

const BillNumberEditor = ({ value, row, onValueChange, autoFocus, onBlur, onKeyDown, disabled }) => {
  const duplicate =
    row.docNumberDuplicate &&
    String(row.invoiceNumber || '')
      .trim()
      .toLowerCase() ===
      String(value || '')
        .trim()
        .toLowerCase();
  return (
    <TextField
      autoFocus={autoFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      size="small"
      variant="standard"
      value={value || ''}
      onChange={(event) => onValueChange(event.target.value)}
      error={duplicate}
      title={duplicate ? row.docNumberDuplicateMessage || 'Bill Number already exists' : undefined}
      disabled={disabled}
      fullWidth
    />
  );
};
const BillNumberProvider = (props) => <DataTypeProvider {...props} editorComponent={BillNumberEditor} />;

const MoneyFormatter = ({ value, row, column }) => {
  if (!Number.isFinite(Number(value)) || value === '') return <span>-</span>;
  try {
    const ratePrecision = column?.name === 'productServiceRate';
    return (
      <span>
        {/* eslint-disable-next-line no-undef */}
        {new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: row.currency || 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: ratePrecision ? 4 : 2
        }).format(value)}
      </span>
    );
  } catch (_error) {
    return <span>{value}</span>;
  }
};

const NumberEditor = ({ value, row, onValueChange, column, autoFocus, onBlur, onKeyDown, disabled }) => {
  const requiredValueMissing =
    column?.name === 'productServiceRate' && (value === '' || value === null || value === undefined);
  const validatesLinePrice = column?.name === 'productServiceRate' || column?.name === 'amount';
  const editedUnitPrice = column?.name === 'productServiceRate' ? value : row?.productServiceRate;
  const editedLineAmount = column?.name === 'amount' ? value : row?.amount;
  const lineAmountInvalid = validatesLinePrice && !isLineAmountValid(row?.quantity, editedUnitPrice, editedLineAmount);
  const priceInvalid = lineAmountInvalid || (validatesLinePrice && row?.invoiceTotalsValid === false);
  const validationMessage = lineAmountInvalid
    ? 'Quantity × Product Service Rate must equal the GST-exclusive Amount. Check GST inclusion or pack/unit pricing.'
    : row?.invoiceTotalsMessage;
  return (
    <TextField
      autoFocus={autoFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      type="number"
      size="small"
      variant="standard"
      value={value ?? ''}
      onChange={(event) => onValueChange(event.target.value)}
      inputProps={{ step: column?.name === 'productServiceRate' ? '0.0001' : '0.01' }}
      error={requiredValueMissing || priceInvalid}
      title={requiredValueMissing ? 'Product Service Rate is required' : priceInvalid ? validationMessage : undefined}
      disabled={disabled}
      fullWidth
    />
  );
};
const MoneyProvider = (props) => (
  <DataTypeProvider {...props} formatterComponent={MoneyFormatter} editorComponent={NumberEditor} />
);
const PercentFormatter = ({ value }) => <span>{value === '' || value == null ? '-' : `${value}%`}</span>;
const PercentProvider = (props) => (
  <DataTypeProvider {...props} formatterComponent={PercentFormatter} editorComponent={NumberEditor} />
);
const NumberProvider = (props) => <DataTypeProvider {...props} editorComponent={NumberEditor} />;

const supplierOptionValue = (option) => option.id || option.value;
const SupplierFormatter = ({ value, row, options }) => {
  const selection = value && typeof value === 'object' ? value : null;
  const linkedValue = selection ? selection.value : row.supplierValue;
  const linked = options.find((option) => String(supplierOptionValue(option)) === String(linkedValue || ''));
  return <span>{selection?.qboName || linked?.label || linked?.value || value || '-'}</span>;
};
const SupplierEditor = ({
  value,
  row,
  onValueChange,
  onBlur,
  autoFocus,
  disabled,
  options,
  loading,
  onCreateVendor
}) => {
  const [creating, setCreating] = useState(false);
  const selection = value && typeof value === 'object' ? value : null;
  const extractedName = selection?.extractedName || value || row.supplierName || '';
  const linkedValue = selection ? selection.value : row.supplierValue;
  const selectOptions = options.map((option) => ({
    value: supplierOptionValue(option),
    label: option.label || option.value,
    TermRef: option.TermRef || null
  }));
  const selected = selectOptions.find((option) => String(option.value) === String(linkedValue || '')) || null;
  const supplierLinked = row.supplierLinked ?? Boolean(linkedValue);

  const createVendor = async (inputValue) => {
    const displayName = inputValue.trim();
    if (!displayName || creating) return;

    setCreating(true);
    try {
      const created = await onCreateVendor(row, displayName);
      if (!created) return;
      onValueChange({
        extractedName,
        value: created.value,
        qboName: created.label,
        TermRef: created.TermRef
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not create the QBO vendor');
    } finally {
      setCreating(false);
    }
  };

  return (
    <CreatableSelect
      autoFocus={autoFocus}
      onBlur={onBlur}
      isDisabled={disabled || creating}
      isClearable
      isSearchable
      isLoading={loading || creating}
      value={selected}
      options={selectOptions}
      placeholder={extractedName || 'Select QBO supplier'}
      formatCreateLabel={(inputValue) => `Create vendor "${inputValue.trim()}"`}
      isValidNewOption={(inputValue, _selectedOptions, availableOptions) => {
        if (!onCreateVendor) return false;
        const search = inputValue.trim().toLowerCase();
        return Boolean(search) && !availableOptions.some((option) => option.label.toLowerCase().includes(search));
      }}
      onCreateOption={createVendor}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        container: (base) => ({ ...base, minWidth: 210 }),
        control: (base) => ({
          ...base,
          minHeight: 32,
          borderColor: supplierLinked ? base.borderColor : '#d32f2f',
          boxShadow: supplierLinked ? base.boxShadow : '0 0 0 1px #d32f2f'
        })
      }}
      onChange={(option) =>
        onValueChange({
          extractedName,
          value: option?.value || null,
          qboName: option?.label || null,
          TermRef: option?.TermRef || null
        })
      }
    />
  );
};
const SupplierProvider = ({ options, loading, onCreateVendor, ...props }) => (
  <DataTypeProvider
    {...props}
    formatterComponent={(formatterProps) => <SupplierFormatter {...formatterProps} options={options} />}
    editorComponent={(editorProps) => (
      <SupplierEditor {...editorProps} options={options} loading={loading} onCreateVendor={onCreateVendor} />
    )}
  />
);

const BoxshallProductFormatter = ({ value, row, options }) => {
  const selection = value && typeof value === 'object' ? value : null;
  const linkedValue = selection ? selection.value : row.boxshallProductId;
  const linked = options.find((option) => String(option.value) === String(linkedValue || ''));
  return <span>{selection ? selection.name || '-' : linked?.label || value || '-'}</span>;
};
const formatBoxshallProductOption = (option, { context }) => {
  if (context === 'value') return option.label;

  return (
    <div>
      <div>{option.label}</div>
      {option.description && (
        <div style={{ color: '#6b7280', fontSize: '0.75rem', lineHeight: 1.35 }}>{option.description}</div>
      )}
    </div>
  );
};
const BoxshallProductEditor = ({
  value,
  row,
  onValueChange,
  onBlur,
  autoFocus,
  disabled,
  options,
  loading,
  loadError,
  loadOptions,
  onCreateProduct
}) => {
  const [creating, setCreating] = useState(false);
  const selection = value && typeof value === 'object' ? value : null;
  const linkedLocalId = selection ? selection.value : row.boxshallProductId;
  const linkedQboId = selection ? selection.qboId : row.boxshallProductValue;
  const selectedName = selection?.name || (typeof value === 'string' ? value : row.boxshallName);
  const selected =
    options.find(
      (option) =>
        (linkedLocalId && String(option.localId) === String(linkedLocalId)) ||
        (linkedQboId && String(option.qboId) === String(linkedQboId))
    ) ||
    (linkedLocalId || linkedQboId
      ? {
          value: String(linkedLocalId || linkedQboId),
          localId: linkedLocalId || null,
          qboId: linkedQboId || '',
          name: selectedName,
          label: selectedName,
          description: selection?.description || row.boxshallDescription || ''
        }
      : null);
  const productLinked = Boolean(linkedLocalId || linkedQboId);

  const createProduct = async (inputValue) => {
    const name = inputValue.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await onCreateProduct(row, row.lineIndex, name, row);
      if (created) onValueChange(created);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not create the Boxshall Product');
    } finally {
      setCreating(false);
    }
  };

  return (
    <AsyncPaginateCreatable
      autoFocus={autoFocus}
      onBlur={onBlur}
      isDisabled={disabled || creating}
      isClearable
      isSearchable
      isLoading={loading || creating || undefined}
      value={selected}
      loadOptions={loadOptions}
      additional={{ skip: 0 }}
      defaultOptions={options}
      defaultAdditional={{ skip: 50 }}
      debounceTimeout={400}
      formatOptionLabel={formatBoxshallProductOption}
      noOptionsMessage={() => loadError || 'No Boxshall Products found'}
      placeholder={typeof value === 'string' && value ? value : 'Select Boxshall Product'}
      formatCreateLabel={(inputValue) => `Add new product for "${inputValue.trim()}"`}
      isValidNewOption={(inputValue, _selectedOptions, availableOptions) => {
        if (!onCreateProduct) return false;
        const search = inputValue.trim().toLowerCase();
        return Boolean(search) && !availableOptions.some((option) => option.label.toLowerCase() === search);
      }}
      onCreateOption={createProduct}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        container: (base) => ({ ...base, minWidth: 210 }),
        control: (base) => ({
          ...base,
          minHeight: 32,
          borderColor: productLinked ? base.borderColor : '#d32f2f',
          boxShadow: productLinked ? base.boxShadow : '0 0 0 1px #d32f2f'
        })
      }}
      onChange={(option) =>
        onValueChange(
          option
            ? {
                value: option.localId,
                qboId: option.qboId,
                name: option.name,
                description: option.description
              }
            : { value: null, qboId: null, name: '', description: row.boxshallDescription }
        )
      }
    />
  );
};
const BoxshallProductProvider = ({ products, loading, loadError, loadOptions, onCreateProduct, ...props }) => {
  const options = products.map(boxshallProductOption).filter((option) => option.value && option.label);
  return (
    <DataTypeProvider
      {...props}
      formatterComponent={(formatterProps) => <BoxshallProductFormatter {...formatterProps} options={options} />}
      editorComponent={(editorProps) => (
        <BoxshallProductEditor
          {...editorProps}
          options={options}
          loading={loading}
          loadError={loadError}
          loadOptions={loadOptions}
          onCreateProduct={onCreateProduct}
        />
      )}
    />
  );
};

const ReferenceFormatter = ({ value }) => <span>{value?.name || '-'}</span>;
const ReferenceEditor = ({ value, row, onValueChange, onBlur, autoFocus, disabled, options, loading, onCreate }) => {
  const [creating, setCreating] = useState(false);
  const selectOptions = options.map((option) => ({ value: String(option.value), label: option.name }));
  const selected =
    selectOptions.find((option) => option.value === String(value?.value || '')) ||
    (value?.value ? { value: String(value.value), label: value.name } : null);
  const SelectComponent = onCreate ? CreatableSelect : ReactSelect;
  const createReference = async (inputValue) => {
    const name = inputValue.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await onCreate(row, row.lineIndex, name);
      if (created) onValueChange(created);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not create the QBO customer');
    } finally {
      setCreating(false);
    }
  };
  return (
    <SelectComponent
      autoFocus={autoFocus}
      onBlur={onBlur}
      isDisabled={disabled || creating}
      isClearable
      isSearchable
      isLoading={loading || creating}
      value={selected}
      options={selectOptions}
      formatCreateLabel={(inputValue) => `Add new customer for "${inputValue.trim()}"`}
      isValidNewOption={(inputValue, _selectedOptions, availableOptions) => {
        if (!onCreate) return false;
        const search = inputValue.trim().toLowerCase();
        return Boolean(search) && !availableOptions.some((option) => option.label.toLowerCase() === search);
      }}
      onCreateOption={createReference}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        container: (base) => ({ ...base, minWidth: 210 }),
        control: (base) => ({ ...base, minHeight: 32 })
      }}
      onChange={(option) => onValueChange(option ? { value: String(option.value), name: option.label } : null)}
    />
  );
};
const ReferenceProvider = ({ options, loading, onCreate, ...props }) => (
  <DataTypeProvider
    {...props}
    formatterComponent={ReferenceFormatter}
    editorComponent={(editorProps) => (
      <ReferenceEditor {...editorProps} options={options} loading={loading} onCreate={onCreate} />
    )}
  />
);

const BillableFormatter = ({ value }) => <span>{value ? 'Billable' : 'Not billable'}</span>;
const BillableEditor = ({ value, onValueChange, autoFocus, onBlur, onKeyDown, disabled }) => (
  <Select
    autoFocus={autoFocus}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    disabled={disabled}
    size="small"
    variant="standard"
    value={value ? 'true' : 'false'}
    onChange={(event) => onValueChange(event.target.value === 'true')}
    fullWidth
  >
    <MenuItem value="true">Billable</MenuItem>
    <MenuItem value="false">Not billable</MenuItem>
  </Select>
);
const BillableProvider = (props) => (
  <DataTypeProvider {...props} formatterComponent={BillableFormatter} editorComponent={BillableEditor} />
);

const StatusFormatter = ({ value }) => {
  const label = value === 'ReviewRequired' || value === 'RequiresReview' ? 'Requires review' : value;
  const color = value === 'Processed' || value === 'Approved' ? 'success' : value === 'Failed' ? 'error' : 'warning';
  return <Chip label={label || '-'} color={color} size="small" variant="outlined" />;
};

const ROW_ACTION_SELECTOR = 'button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]';
const DOUBLE_CLICK_DELAY = 500;
const DOUBLE_CLICK_DISTANCE = 8;

const FocusableCell = ({ onClick, ...props }) => {
  const purchaseTransferFailed = props.row?.purchaseTransfer?.status === 'Failed';
  const supplierUnlinked =
    props.column?.name === 'supplierName' && !(props.row?.supplierLinked ?? Boolean(props.row?.supplierValue));
  const boxshallProductUnlinked =
    props.column?.name === 'boxshallName' &&
    !(props.row?.boxshallProductLinked ?? Boolean(props.row?.boxshallProductId));
  const duplicateBillNumber = props.column?.name === 'invoiceNumber' && props.row?.docNumberDuplicate;
  const productServiceRateMissing =
    props.column?.name === 'productServiceRate' &&
    (props.row?.productServiceRate === '' ||
      props.row?.productServiceRate === null ||
      props.row?.productServiceRate === undefined);
  const lineAmountInvalid = !isLineAmountValid(props.row?.quantity, props.row?.productServiceRate, props.row?.amount);
  const validatesPrice = ['productServiceRate', 'amount', 'calculatedTotal'].includes(props.column?.name);
  const priceInvalid = validatesPrice && (lineAmountInvalid || props.row?.invoiceTotalsValid === false);
  const priceValidationMessage = lineAmountInvalid
    ? 'Quantity × Product Service Rate must equal the GST-exclusive Amount. Check GST inclusion or pack/unit pricing.'
    : props.row?.invoiceTotalsMessage;
  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick?.();
  };
  return (
    <Table.Cell
      {...props}
      tabIndex={0}
      // Starting on focus replaces the cell before the first click completes,
      // which prevents the row from receiving a subsequent double-click.
      onClick={onClick}
      onKeyDown={handleKeyDown}
      title={
        purchaseTransferFailed
          ? props.row.purchaseTransfer.errorMessage || 'This invoice line could not be moved to Purchases'
          : duplicateBillNumber
          ? props.row.docNumberDuplicateMessage || 'Bill Number already exists'
          : supplierUnlinked
          ? 'Supplier is not linked to a QBO vendor'
          : boxshallProductUnlinked
          ? 'Boxshall Name is not linked to a Boxshall Product'
          : productServiceRateMissing
          ? 'Product Service Rate is required'
          : priceInvalid
          ? priceValidationMessage
          : props.title
      }
      style={{
        ...props.style,
        ...((purchaseTransferFailed ||
          supplierUnlinked ||
          boxshallProductUnlinked ||
          duplicateBillNumber ||
          productServiceRateMissing ||
          priceInvalid) && { boxShadow: 'inset 0 0 0 1px #d32f2f' })
      }}
    />
  );
};

const ClickableInvoiceRow = ({
  row: rowProp,
  tableRow,
  onOpen,
  onMouseDownCapture,
  highlighted,
  selectByRowClick,
  onToggle,
  ...props
}) => {
  const row = rowProp || tableRow?.row;
  const handleDoubleClick = (event) => {
    if (!row || event.target.closest(ROW_ACTION_SELECTOR)) return;
    onOpen(row.id);
  };
  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(row.id);
  };
  const handleClick = (event) => {
    if (!selectByRowClick) return;
    event.stopPropagation();
    onToggle?.();
  };
  return (
    <Table.Row
      {...props}
      row={row}
      tableRow={tableRow}
      tabIndex={row ? 0 : undefined}
      onMouseDownCapture={(event) => onMouseDownCapture(row, event)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      selected={highlighted}
      aria-label={row ? `Open ${row.invoiceNumber || 'bill'} details` : undefined}
      style={{ ...props.style, cursor: row ? 'pointer' : props.style?.cursor }}
    />
  );
};

const PopupEditing = React.memo(
  ({
    onSave,
    customerRefs,
    classRefs,
    taxCodes,
    defaultTaxCode,
    vendorList,
    vendorLoading,
    qboTerms,
    qboTermsLoading,
    boxshallProducts,
    boxshallProductsLoading,
    boxshallProductsError,
    loadBoxshallProductOptions,
    referencesLoading,
    taxCodeLoading,
    onCreateBoxshallProduct,
    onCreateCustomer,
    onCheckDocNumbers,
    onUploadManualInvoiceFile
  }) => (
    <Plugin>
      <Template name="popupEditing">
        <TemplateConnector>
          {({ rows, getRowId, editingRowIds }, { stopEditRows, cancelChangedRows }) => {
            if (!editingRowIds.length) return null;
            const [rowId] = editingRowIds;
            const row = rows.find((item) => getRowId(item) === rowId);
            if (!row) return null;
            const close = () => {
              stopEditRows({ rowIds: editingRowIds });
              cancelChangedRows({ rowIds: editingRowIds });
            };
            const apply = async (draft, invoiceFile) => {
              const saved = await onSave(rowId, draft, false);
              if (!saved) return;
              if (invoiceFile) {
                const uploaded = await onUploadManualInvoiceFile(row, invoiceFile);
                if (!uploaded) return;
              }
              close();
            };
            return (
              <InvoiceDialogContent
                open
                row={row}
                customerRefs={customerRefs}
                classRefs={classRefs}
                taxCodes={taxCodes}
                defaultTaxCode={defaultTaxCode}
                vendorList={vendorList}
                vendorLoading={vendorLoading}
                qboTerms={qboTerms}
                qboTermsLoading={qboTermsLoading}
                boxshallProducts={boxshallProducts}
                boxshallProductsLoading={boxshallProductsLoading}
                boxshallProductsError={boxshallProductsError}
                loadBoxshallProductOptions={loadBoxshallProductOptions}
                referencesLoading={referencesLoading}
                taxCodeLoading={taxCodeLoading}
                onCreateBoxshallProduct={onCreateBoxshallProduct}
                onCreateCustomer={onCreateCustomer}
                onCheckDocNumbers={onCheckDocNumbers}
                onApplyChanges={apply}
                onCancelChanges={close}
              />
            );
          }}
        </TemplateConnector>
      </Template>
      <Template name="root">
        <TemplatePlaceholder />
        <TemplatePlaceholder name="popupEditing" />
      </Template>
    </Plugin>
  )
);
PopupEditing.displayName = 'InvoicePopupEditing';

const DEFAULT_BULK_ACTION_BY_STATUS = {
  RequiresReview: 'approve',
  Approved: 'submit'
};

const InvoiceTable = ({
  invoices,
  customerRefs,
  classRefs,
  taxCodes,
  defaultTaxCode,
  defaultItemAccounts,
  vendorList,
  vendorLoading,
  qboTerms,
  qboTermsLoading,
  boxshallProducts,
  boxshallProductsLoading,
  boxshallProductsError,
  loadBoxshallProductOptions,
  referencesLoading,
  taxCodeLoading,
  loading,
  activeStatus,
  total,
  page,
  pageSize,
  search,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onRefresh,
  onOpenPdf,
  onReprocess,
  onApprove,
  onApproveSelected,
  onDeleteSelected,
  onSubmitSelected,
  onCheckDocNumbers,
  onCreateSupplierVendor,
  onCreateCustomer,
  onCreateBoxshallProduct,
  onPreviewBoxshallProduct,
  onCreate,
  onUpdate,
  onUploadManualInvoiceFile,
  onImport,
  mode = 'default'
}) => {
  const importMode = mode === 'import';
  const tableColumns = importMode ? IMPORT_COLUMNS : COLUMNS;
  const [docNumberChecks, setDocNumberChecks] = useState({});
  const [addedRows, setAddedRows] = useState([]);
  const [pendingInvoiceUpdates, setPendingInvoiceUpdates] = useState(0);
  const [productCreation, setProductCreation] = useState(null);
  const [customerCreation, setCustomerCreation] = useState(null);
  const [vendorCreation, setVendorCreation] = useState(null);

  const requestVendorCreation = useCallback(
    (invoice, initialName) =>
      // eslint-disable-next-line no-undef
      new Promise((resolve) => {
        setVendorCreation({ invoice, initialName, resolve });
      }),
    []
  );

  const cancelVendorCreation = useCallback(() => {
    setVendorCreation((current) => {
      current?.resolve(null);
      return null;
    });
  }, []);

  const saveVendorCreation = useCallback(
    async (values) => {
      if (!vendorCreation) return;
      try {
        const created = await onCreateSupplierVendor(vendorCreation.invoice, values);
        vendorCreation.resolve(created);
        setVendorCreation(null);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not create the QBO vendor');
        throw error;
      }
    },
    [onCreateSupplierVendor, vendorCreation]
  );

  const requestCustomerCreation = useCallback(
    (invoice, lineIndex, initialName) =>
      // eslint-disable-next-line no-undef
      new Promise((resolve) => {
        setCustomerCreation({ invoice, lineIndex, initialName, resolve });
      }),
    []
  );

  const cancelCustomerCreation = useCallback(() => {
    setCustomerCreation((current) => {
      current?.resolve(null);
      return null;
    });
  }, []);

  const saveCustomerCreation = useCallback(
    async (values) => {
      if (!customerCreation) return;
      try {
        const created = await onCreateCustomer(customerCreation.invoice, customerCreation.lineIndex, values);
        customerCreation.resolve(created);
        setCustomerCreation(null);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not create the QBO customer');
        throw error;
      }
    },
    [customerCreation, onCreateCustomer]
  );

  const requestBoxshallProductCreation = useCallback(
    (invoice, lineIndex, initialName, lineItem) =>
      // eslint-disable-next-line no-undef
      new Promise((resolve) => {
        setProductCreation({ invoice, lineIndex, initialName, lineItem, resolve });
      }),
    []
  );

  const cancelBoxshallProductCreation = useCallback(() => {
    setProductCreation((current) => {
      current?.resolve(null);
      return null;
    });
  }, []);

  const saveBoxshallProductCreation = useCallback(
    async (values) => {
      if (!productCreation) return;
      try {
        const created = await onCreateBoxshallProduct(productCreation.invoice, productCreation.lineIndex, values);
        productCreation.resolve(created);
        setProductCreation(null);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not create the Boxshall Product');
        throw error;
      }
    },
    [onCreateBoxshallProduct, productCreation]
  );

  useEffect(() => {
    let active = true;
    const checks = invoices
      .filter(
        (invoice) =>
          !invoice.qboBill?.id &&
          String(invoice.invoiceNumber || '').trim() &&
          (importMode || String(invoice.supplier?.value || '').trim())
      )
      .map((invoice) => ({
        invoiceId: invoice._id,
        docNumber: invoice.invoiceNumber,
        ...(importMode && { databaseOnly: true }),
        ...(invoice.supplier?.value && { supplierValue: invoice.supplier.value })
      }));
    if (!checks.length) {
      setDocNumberChecks({});
      return () => {
        active = false;
      };
    }

    onCheckDocNumbers(checks)
      .then((results) => {
        if (!active) return;
        setDocNumberChecks(
          Object.fromEntries(
            results.map((result) => [
              String(result.invoiceId),
              {
                ...result,
                normalizedDocNumber: String(result.docNumber || '')
                  .trim()
                  .toLowerCase(),
                normalizedSupplierValue: String(result.supplierValue || '').trim()
              }
            ])
          )
        );
      })
      .catch(() => {
        if (active) setDocNumberChecks({});
      });

    return () => {
      active = false;
    };
  }, [importMode, invoices, onCheckDocNumbers]);

  const rows = useMemo(
    () =>
      invoices.flatMap((invoice) => {
        const check = docNumberChecks[String(invoice._id)];
        const currentDocNumber = String(invoice.invoiceNumber || '')
          .trim()
          .toLowerCase();
        const currentSupplierValue = String(invoice.supplier?.value || '').trim();
        const currentCheck =
          check?.normalizedDocNumber === currentDocNumber && check?.normalizedSupplierValue === currentSupplierValue
            ? check
            : null;
        return toGridRows(invoice, defaultTaxCode).map((row) => ({
          ...row,
          docNumberDuplicate: Boolean(currentCheck?.duplicate),
          docNumberDuplicateMessage: currentCheck?.message || ''
        }));
      }),
    [defaultTaxCode, docNumberChecks, invoices]
  );
  const [columnFilters, setColumnFilters] = useLocalStorage('invoiceLineColumnFilters', {});

  const filteredRows = useMemo(() => {
    if (importMode) return rows;
    return rows.filter((row) => {
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) return false;
        }
      }
      return true;
    });
  }, [columnFilters, importMode, rows]);

  const [sorting, setSorting] = useState([{ columnName: 'receivedAt', direction: 'desc' }]);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const editingModeRef = useRef(null);
  const rowClickRef = useRef(null);
  const [columnOrder, setColumnOrder] = useLocalStorage(
    'invoiceLineColumnOrderV5',
    COLUMNS.map(({ name }) => name)
  );
  const [selection, setSelection] = useState([]);
  const [reprocessingInvoiceIds, setReprocessingInvoiceIds] = useState([]);
  const [columnWidths, setColumnWidths] = useLocalStorage('invoiceLineColumnWidthsV5', DEFAULT_WIDTHS);
  const [hiddenColumns, setHiddenColumns] = useLocalStorage('invoiceLineHiddenColumnsV5', ['receivedAt']);
  const exporterRef = useRef(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [partialSelectionDialogOpen, setPartialSelectionDialogOpen] = useState(false);
  const [savedBulkActions, setSavedBulkActions] = useLocalStorage(
    'invoiceBulkActionByStatus',
    DEFAULT_BULK_ACTION_BY_STATUS
  );

  const completeColumnOrder = useMemo(() => {
    const defaultOrder = COLUMNS.map(({ name }) => name);
    const savedOrder = (columnOrder || []).filter(
      (name, index, names) => defaultOrder.includes(name) && names.indexOf(name) === index
    );
    const completedOrder = [...savedOrder];
    defaultOrder.forEach((name, defaultIndex) => {
      if (completedOrder.includes(name)) return;
      const precedingColumn = [...defaultOrder.slice(0, defaultIndex)]
        .reverse()
        .find((columnName) => completedOrder.includes(columnName));
      const insertionIndex = precedingColumn ? completedOrder.indexOf(precedingColumn) + 1 : 0;
      completedOrder.splice(insertionIndex, 0, name);
    });
    return completedOrder;
  }, [columnOrder]);

  const completeColumnWidths = useMemo(() => {
    // eslint-disable-next-line no-undef
    const savedWidths = new Map((columnWidths || []).map((width) => [width.columnName, width]));
    return DEFAULT_WIDTHS.map((defaultWidth) => savedWidths.get(defaultWidth.columnName) || defaultWidth);
  }, [columnWidths]);

  const changeSelection = (sel) => {
    setSelection(sel);
  };

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const availableRowIds = new Set(filteredRows.map(({ id }) => id));
    setSelection((current) => current.filter((rowId) => availableRowIds.has(rowId)));
  }, [filteredRows]);

  const selectedRows = useMemo(() => {
    // eslint-disable-next-line no-undef
    const selectedRowIds = new Set(selection);
    return filteredRows.filter((row) => selectedRowIds.has(row.id));
  }, [filteredRows, selection]);

  const selectedDeletionGroups = useMemo(() => {
    // eslint-disable-next-line no-undef
    const groupsByInvoice = new Map();
    selectedRows.forEach((row) => {
      const invoiceId = row.invoiceId || row._id;
      if (!invoiceId) return;
      const key = String(invoiceId);
      const current = groupsByInvoice.get(key) || {
        invoiceId,
        row,
        lineIndexes: []
      };
      current.lineIndexes.push(row.lineIndex);
      groupsByInvoice.set(key, current);
    });

    return [...groupsByInvoice.values()].map((group) => ({
      ...group,
      // eslint-disable-next-line no-undef
      lineIndexes: [...new Set(group.lineIndexes)].sort((left, right) => left - right)
    }));
  }, [selectedRows]);

  const selectedTotalLabel = useMemo(() => {
    if (!selectedRows.length) return '';
    // eslint-disable-next-line no-undef
    const currencies = [...new Set(selectedRows.map((row) => row.currency || 'AUD'))];
    if (currencies.length !== 1) return 'Mixed currencies';
    if (selectedRows.some((row) => !Number.isFinite(row.calculatedTotal))) return 'Unavailable';
    const selectedTotal = selectedRows.reduce((totalValue, row) => totalValue + row.calculatedTotal, 0);
    try {
      // eslint-disable-next-line no-undef
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currencies[0] }).format(selectedTotal);
    } catch (_error) {
      return selectedTotal.toFixed(2);
    }
  }, [selectedRows]);

  const invalidInvoiceCount = useMemo(
    () =>
      // eslint-disable-next-line no-undef
      new Set(
        rows.filter((row) => row.invoiceTotalsValid === false).map((row) => String(row.invoiceId || row._id || row.id))
      ).size,
    [rows]
  );

  const approvableInvoiceIds = useMemo(
    () => [
      // eslint-disable-next-line no-undef
      ...new Set(
        selectedRows
          .filter(
            (row) =>
              row.approvalStatus === 'RequiresReview' &&
              row.extractionStatus !== 'Processing' &&
              row.extractionStatus !== 'Failed'
          )
          .map((row) => row.invoiceId || row._id)
          .filter(Boolean)
      )
    ],
    [selectedRows]
  );

  const submittableInvoices = useMemo(() => {
    // eslint-disable-next-line no-undef
    const selectionsByInvoice = new Map();
    selectedRows
      .filter(
        (row) => row.approvalStatus === 'Approved' && (!row.qboBill?.id || row.purchaseTransfer?.status !== 'Created')
      )
      .forEach((row) => {
        const invoiceId = row.invoiceId || row._id;
        if (!invoiceId) return;
        const current = selectionsByInvoice.get(String(invoiceId)) || {
          invoiceId,
          invoiceNumber: row.invoiceNumber || invoiceId,
          dueDate: row.dueDate,
          docNumberDuplicate: row.docNumberDuplicate,
          docNumberDuplicateMessage: row.docNumberDuplicateMessage,
          purchaseTransferRetry: Boolean(row.qboBill?.id),
          selectedLineIndexes: [],
          totalLineItems: row.lineItems?.length || 1
        };
        current.selectedLineIndexes.push(row.lineIndex);
        selectionsByInvoice.set(String(invoiceId), current);
      });

    return [...selectionsByInvoice.values()].map((selection) => ({
      ...selection,
      // eslint-disable-next-line no-undef
      selectedLineIndexes: [...new Set(selection.selectedLineIndexes)].sort((a, b) => a - b)
    }));
  }, [selectedRows]);

  const partiallySelectedInvoices = useMemo(
    () =>
      submittableInvoices.filter(
        ({ purchaseTransferRetry, selectedLineIndexes, totalLineItems }) =>
          !purchaseTransferRetry && selectedLineIndexes.length < totalLineItems
      ),
    [submittableInvoices]
  );

  const submittableInvoicesMissingDueDate = useMemo(
    () =>
      submittableInvoices.filter(
        ({ dueDate, purchaseTransferRetry }) =>
          !purchaseTransferRetry && (!dueDate || Number.isNaN(new Date(dueDate).getTime()))
      ),
    [submittableInvoices]
  );

  const submittableInvoicesWithDuplicateDocNumber = useMemo(
    () =>
      submittableInvoices.filter(
        ({ docNumberDuplicate, purchaseTransferRetry }) => !purchaseTransferRetry && docNumberDuplicate
      ),
    [submittableInvoices]
  );

  const selectedRowsMissingBoxshallProduct = useMemo(
    () => selectedRows.filter((row) => row.approvalStatus === 'Approved' && !row.boxshallProductValue),
    [selectedRows]
  );

  const availableBulkActions = BULK_ACTIONS_BY_STATUS[activeStatus] || [];
  const savedBulkAction = savedBulkActions?.[activeStatus];
  const selectedBulkAction = availableBulkActions.some(({ value }) => value === savedBulkAction)
    ? savedBulkAction
    : DEFAULT_BULK_ACTION_BY_STATUS[activeStatus];

  const changeBulkAction = useCallback(
    (action) => {
      setSavedBulkActions({ ...(savedBulkActions || DEFAULT_BULK_ACTION_BY_STATUS), [activeStatus]: action });
    },
    [activeStatus, savedBulkActions, setSavedBulkActions]
  );

  const columnExtensions = useMemo(
    () => tableColumns.map(({ name }) => ({ columnName: name, editingEnabled: INLINE_COLUMNS.includes(name) })),
    [tableColumns]
  );

  const changeAddedRows = useCallback(
    (nextRows) =>
      setAddedRows(
        nextRows.map((row) => ({
          ...row,
          calculatedTotal: calculateLineTotal(row.quantity, row.productServiceRate),
          currency: row.currency || 'AUD',
          billableStatus: row.billableStatus ?? false,
          taxCode: row.taxCode === undefined ? defaultTaxCode : row.taxCode
        }))
      ),
    [defaultTaxCode]
  );

  const validateNewRow = useCallback((row) => {
    const missingColumns = REQUIRED_NEW_ROW_COLUMNS.filter((columnName) => isEmptyRequiredValue(row[columnName]));
    if (!missingColumns.length) return true;
    toast.error('Please fill all required invoice fields');
    return false;
  }, []);

  const updateInvoice = useCallback(
    async (invoiceId, payload) => {
      setPendingInvoiceUpdates((count) => count + 1);
      try {
        return await onUpdate(invoiceId, payload);
      } finally {
        setPendingInvoiceUpdates((count) => Math.max(0, count - 1));
      }
    },
    [onUpdate]
  );

  const saveRow = useCallback(
    async (_rowId, row, replaceGridLine = true, referenceFieldsToPropagate = []) => {
      try {
        await updateInvoice(row.invoiceId || row._id, rowToPayload(row, replaceGridLine, referenceFieldsToPropagate));
        toast.success('Bill updated');
        return true;
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not update bill');
        return false;
      }
    },
    [updateInvoice]
  );

  const uploadManualInvoiceFile = useCallback(
    async (row, file) => {
      const invoiceId = row.invoiceId || row._id;
      try {
        await onUploadManualInvoiceFile(invoiceId, file);
        toast.success('Invoice file uploaded');
        return true;
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not upload the invoice file');
        return false;
      }
    },
    [onUploadManualInvoiceFile]
  );

  const addLine = useCallback(
    async (row) => {
      if (!row) return;
      try {
        const lineItems = [...(row.lineItems || []), emptyLineItem(row.currency || 'AUD', defaultTaxCode)];
        await updateInvoice(row.invoiceId || row._id, rowToPayload({ ...row, lineItems }));
        toast.success('Line added');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not add the line');
      }
    },
    [defaultTaxCode, updateInvoice]
  );

  const commitChanges = useCallback(
    async ({ added, changed, deleted }) => {
      if (added?.length) {
        const [newRow] = added;
        if (!validateNewRow(newRow)) {
          // DevExpress clears an added row immediately after commit. Restore it
          // on the next task when validation has rejected the save.
          // eslint-disable-next-line no-undef
          window.setTimeout(() => setAddedRows(added), 0);
          return;
        }
        setPendingInvoiceUpdates((count) => count + 1);
        try {
          await onCreate(rowToPayload(newRow, true));
          toast.success('Bill created');
        } catch (error) {
          toast.error(error.response?.data?.message || 'Could not create bill');
          setAddedRows(added);
        } finally {
          setPendingInvoiceUpdates((count) => Math.max(0, count - 1));
        }
        return;
      }

      if (deleted?.length) {
        const deletedRows = deleted
          .map((rowId) => rows.find((row) => String(row.id) === String(rowId)))
          .filter(Boolean);
        // eslint-disable-next-line no-undef
        const rowsByInvoice = new Map();
        deletedRows.forEach((row) => {
          const invoiceId = row.invoiceId || row._id;
          if (!invoiceId) return;
          const group = rowsByInvoice.get(String(invoiceId)) || { invoiceId, row, lineIndexes: [] };
          group.lineIndexes.push(row.lineIndex);
          rowsByInvoice.set(String(invoiceId), group);
        });

        if (rowsByInvoice.size) {
          const deletionGroups = [...rowsByInvoice.values()];
          const invoicesToDelete = deletionGroups.filter(({ row, lineIndexes }) =>
            deletesWholeInvoice(row, lineIndexes)
          );
          const lineItemsToDelete = deletionGroups
            .filter(({ row, lineIndexes }) => !deletesWholeInvoice(row, lineIndexes))
            .reduce((count, { lineIndexes }) => count + lineIndexes.length, 0);
          const confirmationMessage =
            invoicesToDelete.length === 1 && !lineItemsToDelete
              ? 'Delete this bill? This cannot be undone.'
              : !invoicesToDelete.length
              ? `Delete ${
                  lineItemsToDelete === 1 ? 'this line item' : `${lineItemsToDelete} line items`
                }? This cannot be undone.`
              : `Delete ${invoicesToDelete.length} ${invoicesToDelete.length === 1 ? 'bill' : 'bills'}${
                  lineItemsToDelete
                    ? ` and ${lineItemsToDelete} line ${lineItemsToDelete === 1 ? 'item' : 'items'}`
                    : ''
                }? This cannot be undone.`;
          // eslint-disable-next-line no-undef
          const confirmed = window.confirm(confirmationMessage);
          if (confirmed) {
            if (invoicesToDelete.length) {
              await onDeleteSelected(invoicesToDelete.map(({ invoiceId }) => invoiceId));
            }
            let deletedCount = 0;
            for (const { invoiceId, row, lineIndexes } of deletionGroups) {
              if (deletesWholeInvoice(row, lineIndexes)) continue;
              try {
                await updateInvoice(invoiceId, rowToPayloadAfterLineItemDeletion(row, lineIndexes));
                deletedCount += lineIndexes.length;
              } catch (error) {
                toast.error(error.response?.data?.message || 'Could not delete the line item');
              }
            }
            if (deletedCount) {
              toast.success(`${deletedCount} line ${deletedCount === 1 ? 'item' : 'items'} deleted`);
            }
          }
        }
      }

      if (!changed) return;
      for (const [rowId, changes] of Object.entries(changed)) {
        if (!changes || typeof changes !== 'object') continue;
        const row = rows.find((item) => String(item.id) === String(rowId));
        if (row) {
          let changesWithVendorDueDate = changes;
          const supplierChanged = Object.prototype.hasOwnProperty.call(changes, 'supplierName');
          const dueDateChanged = Object.prototype.hasOwnProperty.call(changes, 'dueDate');
          if (dueDateChanged) {
            changesWithVendorDueDate = { ...changes, dueDateSource: 'manual' };
          } else if (supplierChanged) {
            const supplierSelection =
              changes.supplierName && typeof changes.supplierName === 'object' ? changes.supplierName : null;
            const supplierId = supplierSelection?.value;
            const vendor = vendorList.find(
              (option) => String(supplierOptionValue(option)) === String(supplierId || '')
            );
            const vendorDueDate = dueDateFromVendorTerm(
              changes.invoiceDate ?? row.invoiceDate,
              '',
              supplierSelection?.TermRef || vendor?.TermRef
            );
            if (vendorDueDate) {
              changesWithVendorDueDate = {
                ...changes,
                dueDate: vendorDueDate,
                dueDateSource: 'vendor'
              };
            }
          }
          const referenceFields = Object.prototype.hasOwnProperty.call(changes, 'taxCode') ? ['taxCode'] : [];
          await saveRow(rowId, { ...row, ...changesWithVendorDueDate }, true, referenceFields);
        }
      }
    },
    [onCreate, onDeleteSelected, rows, saveRow, updateInvoice, validateNewRow, vendorList]
  );

  const openPopup = useCallback((rowId) => {
    editingModeRef.current = 'popup';
    setEditingCellIds([]);
    setEditingRowIds([rowId]);
  }, []);

  const handleRowMouseDown = useCallback(
    (row, event) => {
      if (!row || event.button !== 0 || event.target.closest(ROW_ACTION_SELECTOR)) {
        rowClickRef.current = null;
        return;
      }

      const currentClick = {
        rowId: row.id,
        time: Date.now(),
        x: event.clientX,
        y: event.clientY
      };
      const previousClick = rowClickRef.current;
      const isRapidSecondClick =
        previousClick &&
        previousClick.rowId === currentClick.rowId &&
        currentClick.time - previousClick.time <= DOUBLE_CLICK_DELAY &&
        Math.abs(currentClick.x - previousClick.x) <= DOUBLE_CLICK_DISTANCE &&
        Math.abs(currentClick.y - previousClick.y) <= DOUBLE_CLICK_DISTANCE;

      rowClickRef.current = isRapidSecondClick ? null : currentClick;
      if (!isRapidSecondClick) return;

      event.preventDefault();
      event.stopPropagation();
      openPopup(row.id);
    },
    [openPopup]
  );

  const startPopupEditing = useCallback(() => {
    editingModeRef.current = 'popup';
    setEditingCellIds([]);
  }, []);

  const reprocessRow = useCallback(
    async (row) => {
      const invoiceId = String(row.invoiceId || row._id || '');
      if (!invoiceId || reprocessingInvoiceIds.includes(invoiceId)) return;
      setReprocessingInvoiceIds((current) => [...current, invoiceId]);
      try {
        await onReprocess(row);
      } finally {
        setReprocessingInvoiceIds((current) => current.filter((id) => id !== invoiceId));
      }
    },
    [onReprocess, reprocessingInvoiceIds]
  );

  const ActionFormatter = useCallback(
    ({ row }) => {
      const invoiceId = String(row.invoiceId || row._id || '');
      const reprocessing = reprocessingInvoiceIds.includes(invoiceId);
      return (
        <div className="tw-flex tw-items-center tw-gap-1">
          <Tooltip title="Open PDF">
            <IconButton size="small" onClick={() => onOpenPdf(row)}>
              <FileOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {['Processing', 'Failed', 'ReviewRequired'].includes(row.extractionStatus) && (
            <Tooltip title={reprocessing ? 'Reprocessing bill' : 'Reprocess bill'}>
              <span>
                <IconButton size="small" disabled={reprocessing} onClick={() => reprocessRow(row)}>
                  {reprocessing ? <CircularProgress size={18} /> : <ReplayIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!importMode &&
            row.approvalStatus === 'RequiresReview' &&
            row.extractionStatus !== 'Processing' &&
            row.extractionStatus !== 'Failed' && (
              <Tooltip title="Approve bill">
                <IconButton size="small" color="success" onClick={() => onApprove(row)}>
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
        </div>
      );
    },
    [importMode, onApprove, onOpenPdf, reprocessRow, reprocessingInvoiceIds]
  );

  const startExport = useCallback((options) => exporterRef.current.exportGrid(options), []);

  const approveSelected = useCallback(async () => {
    if (!approvableInvoiceIds.length || bulkAction) return;
    setBulkAction('approve');
    try {
      const completed = await onApproveSelected(approvableInvoiceIds);
      if (completed) setSelection([]);
    } finally {
      setBulkAction(null);
    }
  }, [approvableInvoiceIds, bulkAction, onApproveSelected]);

  const deleteSelected = useCallback(async () => {
    if (!selectedDeletionGroups.length || bulkAction) return;
    const invoicesToDelete = selectedDeletionGroups.filter(({ row, lineIndexes }) =>
      deletesWholeInvoice(row, lineIndexes)
    );
    const lineGroupsToUpdate = selectedDeletionGroups.filter(
      ({ row, lineIndexes }) => !deletesWholeInvoice(row, lineIndexes)
    );
    const lineItemsToDelete = lineGroupsToUpdate.reduce((count, { lineIndexes }) => count + lineIndexes.length, 0);
    const confirmationMessage =
      invoicesToDelete.length && !lineItemsToDelete
        ? `Delete ${invoicesToDelete.length} selected ${
            invoicesToDelete.length === 1 ? 'bill' : 'bills'
          }? This cannot be undone.`
        : !invoicesToDelete.length
        ? `Delete ${
            lineItemsToDelete === 1 ? 'this line item' : `${lineItemsToDelete} line items`
          }? This cannot be undone.`
        : `Delete ${invoicesToDelete.length} ${
            invoicesToDelete.length === 1 ? 'bill' : 'bills'
          } and ${lineItemsToDelete} line ${lineItemsToDelete === 1 ? 'item' : 'items'}? This cannot be undone.`;
    // eslint-disable-next-line no-undef
    if (!window.confirm(confirmationMessage)) return;

    setBulkAction('delete');
    try {
      let completed = true;
      if (invoicesToDelete.length) {
        completed = await onDeleteSelected(invoicesToDelete.map(({ invoiceId }) => invoiceId));
      }
      let deletedLineCount = 0;
      for (const { invoiceId, row, lineIndexes } of lineGroupsToUpdate) {
        try {
          await updateInvoice(invoiceId, rowToPayloadAfterLineItemDeletion(row, lineIndexes));
          deletedLineCount += lineIndexes.length;
        } catch (error) {
          completed = false;
          toast.error(error.response?.data?.message || 'Could not delete the line item');
        }
      }
      if (deletedLineCount) {
        toast.success(`${deletedLineCount} line ${deletedLineCount === 1 ? 'item' : 'items'} deleted`);
      }
      if (completed) setSelection([]);
    } finally {
      setBulkAction(null);
    }
  }, [bulkAction, onDeleteSelected, selectedDeletionGroups, updateInvoice]);

  const submitWithMode = useCallback(
    async (selectionMode) => {
      if (!submittableInvoices.length || bulkAction) return;
      setBulkAction('submit');
      try {
        const invoiceSelections = submittableInvoices.map(({ invoiceId, selectedLineIndexes, totalLineItems }) => ({
          invoiceId,
          selectedLineIndexes,
          ...(selectionMode === 'selected' &&
            selectedLineIndexes.length < totalLineItems && { confirmPartialSelection: true })
        }));
        const completed = await onSubmitSelected(invoiceSelections, selectionMode);
        if (completed) setSelection([]);
      } finally {
        setBulkAction(null);
        setPartialSelectionDialogOpen(false);
      }
    },
    [bulkAction, onSubmitSelected, submittableInvoices]
  );

  const submitSelected = useCallback(async () => {
    if (!submittableInvoices.length || bulkAction) return;
    if (submittableInvoicesWithDuplicateDocNumber.length) {
      const duplicate = submittableInvoicesWithDuplicateDocNumber[0];
      toast.error(
        duplicate.docNumberDuplicateMessage ||
          `Bill ${duplicate.invoiceNumber}: this Bill Number is already in use. Change it before submitting.`
      );
      return;
    }
    if (submittableInvoicesMissingDueDate.length) {
      toast.error(`Bill ${submittableInvoicesMissingDueDate[0].invoiceNumber}: select a Due Date before submitting`);
      return;
    }
    if (selectedRowsMissingBoxshallProduct.length) {
      const firstMissingRow = selectedRowsMissingBoxshallProduct[0];
      const itemLabel =
        firstMissingRow.productService ||
        firstMissingRow.productServiceDescription ||
        `line ${firstMissingRow.lineIndex + 1}`;
      const additionalMissingCount = selectedRowsMissingBoxshallProduct.length - 1;
      toast.error(
        `Bill ${firstMissingRow.invoiceNumber}: select a Boxshall Product for item ${itemLabel} (line ${
          firstMissingRow.lineIndex + 1
        }) before submitting${
          additionalMissingCount
            ? `. ${additionalMissingCount} more selected ${
                additionalMissingCount === 1 ? 'item also needs' : 'items also need'
              } a Boxshall Product.`
            : ''
        }`
      );
      return;
    }
    if (partiallySelectedInvoices.length) {
      setPartialSelectionDialogOpen(true);
      return;
    }
    await submitWithMode('selected');
  }, [
    bulkAction,
    partiallySelectedInvoices.length,
    selectedRowsMissingBoxshallProduct,
    submittableInvoicesWithDuplicateDocNumber,
    submittableInvoicesMissingDueDate,
    submitWithMode,
    submittableInvoices.length
  ]);

  const getBulkActionCount = useCallback(
    (action) => {
      if (action === 'approve') return approvableInvoiceIds.length;
      if (action === 'submit') return submittableInvoices.length;
      if (action === 'delete') return selectedRows.length;
      return 0;
    },
    [approvableInvoiceIds.length, selectedRows.length, submittableInvoices.length]
  );

  const runBulkAction = useCallback(
    (action) => {
      if (action === 'approve') return approveSelected();
      if (action === 'submit') return submitSelected();
      if (action === 'delete') return deleteSelected();
      return undefined;
    },
    [approveSelected, deleteSelected, submitSelected]
  );

  const toolbarRootComponent = useCallback(
    (props) => (
      <CustomToolbarRoot
        {...props}
        total={total}
        onRefresh={onRefresh}
        loading={loading}
        activeStatus={activeStatus}
        selectedCount={selection.length}
        selectedTotalLabel={selectedTotalLabel}
        invalidInvoiceCount={invalidInvoiceCount}
        selectedBulkAction={selectedBulkAction}
        onBulkActionChange={changeBulkAction}
        onBulkAction={runBulkAction}
        getBulkActionCount={getBulkActionCount}
        bulkAction={bulkAction}
        onImport={onImport}
      />
    ),
    [
      activeStatus,
      bulkAction,
      changeBulkAction,
      getBulkActionCount,
      loading,
      onRefresh,
      runBulkAction,
      selectedBulkAction,
      selectedTotalLabel,
      selection.length,
      total,
      invalidInvoiceCount,
      onImport
    ]
  );
  const saveExport = (workbook) => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      // eslint-disable-next-line no-undef
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Bills.xlsx');
    });
  };

  return (
    <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
      <div style={{ position: 'relative' }}>
        <Paper>
          <Grid rows={filteredRows} columns={tableColumns} getRowId={getRowId}>
            <EditingState
              addedRows={addedRows}
              onAddedRowsChange={changeAddedRows}
              editingCellIds={editingCellIds}
              onEditingCellIdsChange={(ids) => {
                if (editingModeRef.current === 'popup') return;
                const editable = ids.filter((item) => INLINE_COLUMNS.includes(item.columnName));
                editingModeRef.current = editable.length ? 'inline' : null;
                setEditingCellIds(editable);
              }}
              editingRowIds={editingRowIds}
              onEditingRowIdsChange={(ids) => {
                setEditingRowIds(ids);
                if (ids.length) {
                  editingModeRef.current = 'popup';
                  setEditingCellIds([]);
                } else if (editingModeRef.current === 'popup') {
                  editingModeRef.current = null;
                }
              }}
              onCommitChanges={commitChanges}
              columnExtensions={columnExtensions}
            />
            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <IntegratedSorting />
            <SelectionState selection={selection} onSelectionChange={changeSelection} />
            <IntegratedSelection />

            <SearchState value={search} onValueChange={onSearchChange} />
            <PagingState
              currentPage={page - 1}
              onCurrentPageChange={(value) => onPageChange(value + 1)}
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
            />
            <CustomPaging totalCount={total} />
            <SupplierProvider
              for={['supplierName']}
              options={vendorList}
              loading={vendorLoading}
              onCreateVendor={requestVendorCreation}
            />
            <BoxshallProductProvider
              for={['boxshallName']}
              products={boxshallProducts}
              loading={boxshallProductsLoading}
              loadError={boxshallProductsError}
              loadOptions={loadBoxshallProductOptions}
              onCreateProduct={requestBoxshallProductCreation}
            />
            <DateProvider for={['receivedAt', 'invoiceDate']} />
            <DueDateProvider for={['dueDate']} terms={qboTerms} loading={qboTermsLoading} />
            <BillNumberProvider for={['invoiceNumber']} />
            <MoneyProvider for={['productServiceRate', 'amount', 'calculatedTotal']} />
            <NumberProvider for={['quantity']} />
            <PercentProvider for={['markupPercent']} />
            <BillableProvider for={['billableStatus']} />
            <ReferenceProvider for={['taxCode']} options={taxCodes} loading={taxCodeLoading} />
            <ReferenceProvider
              for={['customerRef']}
              options={customerRefs}
              loading={referencesLoading}
              onCreate={requestCustomerCreation}
            />
            <ReferenceProvider for={['classRef']} options={classRefs} loading={referencesLoading} />
            <DataTypeProvider for={['extractionStatus', 'approvalStatus']} formatterComponent={StatusFormatter} />
            <DataTypeProvider for={['actions']} formatterComponent={ActionFormatter} />
            <DragDropProvider />
            <Table
              rowComponent={(props) => (
                <ClickableInvoiceRow {...props} onOpen={openPopup} onMouseDownCapture={handleRowMouseDown} />
              )}
              cellComponent={FocusableCell}
              containerComponent={ResponsiveTableContainer}
              headComponent={StickyTableHead}
              messages={{
                noData: loading ? 'Loading bills…' : 'No data'
              }}
            />
            <TableColumnResizing columnWidths={completeColumnWidths} onColumnWidthsChange={setColumnWidths} />
            <TableColumnReordering
              order={importMode ? tableColumns.map(({ name }) => name) : completeColumnOrder}
              onOrderChange={importMode ? undefined : setColumnOrder}
            />
            <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />
            {!importMode && (
              <TableSelection
                showSelectAll
                highlightRow
                rowComponent={(props) => (
                  <ClickableInvoiceRow {...props} onOpen={openPopup} onMouseDownCapture={handleRowMouseDown} />
                )}
              />
            )}
            <TableColumnVisibility
              hiddenColumnNames={importMode ? [] : hiddenColumns}
              onHiddenColumnNamesChange={importMode ? undefined : setHiddenColumns}
            />
            {!importMode && <Toolbar rootComponent={toolbarRootComponent} />}
            {!importMode && (
              <SearchPanel messages={{ searchPlaceholder: 'Search supplier, bill number, project or PO' }} />
            )}
            {!importMode && <ColumnChooser />}
            {!importMode && <ExportPanel startExport={startExport} />}
            <TableEditRow cellComponent={NewInvoiceEditCell} />
            <TableEditColumn
              width={90}
              showAddCommand={!addedRows.length}
              showEditCommand={!importMode}
              showDeleteCommand
              cellComponent={(props) => (
                <KebabCommandCell
                  {...props}
                  onPopupEditStart={startPopupEditing}
                  onAddLine={addLine}
                  onValidateNewRow={validateNewRow}
                />
              )}
            />
            <TableInlineCellEditing startEditAction="click" selectTextOnEditStart cellComponent={InvoiceAutoSaveCell} />
            {!importMode && (
              <PopupEditing
                onSave={saveRow}
                customerRefs={customerRefs}
                classRefs={classRefs}
                taxCodes={taxCodes}
                defaultTaxCode={defaultTaxCode}
                vendorList={vendorList}
                vendorLoading={vendorLoading}
                qboTerms={qboTerms}
                qboTermsLoading={qboTermsLoading}
                boxshallProducts={boxshallProducts}
                boxshallProductsLoading={boxshallProductsLoading}
                boxshallProductsError={boxshallProductsError}
                loadBoxshallProductOptions={loadBoxshallProductOptions}
                referencesLoading={referencesLoading}
                taxCodeLoading={taxCodeLoading}
                onCreateBoxshallProduct={requestBoxshallProductCreation}
                onCreateCustomer={requestCustomerCreation}
                onCheckDocNumbers={onCheckDocNumbers}
                onUploadManualInvoiceFile={uploadManualInvoiceFile}
              />
            )}
            {!importMode && <PagingPanel pageSizes={[25, 50, 100]} />}
          </Grid>
          <GridExporter
            ref={exporterRef}
            rows={filteredRows}
            columns={tableColumns}
            hiddenColumnNames={[...hiddenColumns, 'actions']}
            selection={selection}
            getRowId={getRowId}
            onSave={saveExport}
          />
        </Paper>
        {pendingInvoiceUpdates > 0 && (
          <div
            role="status"
            aria-live="polite"
            aria-label="Saving invoice changes"
            style={{
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.72)',
              display: 'flex',
              inset: 0,
              justifyContent: 'center',
              position: 'absolute',
              zIndex: 20
            }}
          >
            <Paper elevation={4} sx={{ alignItems: 'center', display: 'flex', gap: 1.5, px: 2.5, py: 1.5 }}>
              <CircularProgress size={22} />
              <span>Saving bill changes…</span>
            </Paper>
          </div>
        )}
        <InvoiceDialog
          partialSelectionDialogOpen={partialSelectionDialogOpen}
          setPartialSelectionDialogOpen={setPartialSelectionDialogOpen}
          bulkAction={bulkAction}
          partiallySelectedInvoices={partiallySelectedInvoices}
          submitWithMode={submitWithMode}
        />
        {productCreation && (
          <CreateBoxshallProductDialog
            open
            initialName={productCreation.initialName}
            lineItem={productCreation.lineItem}
            classRefs={classRefs}
            taxCodes={taxCodes}
            vendorList={vendorList}
            defaultAccounts={defaultItemAccounts}
            defaultTaxCode={defaultTaxCode}
            onPreview={onPreviewBoxshallProduct}
            onCancel={cancelBoxshallProductCreation}
            onSave={saveBoxshallProductCreation}
          />
        )}
        {vendorCreation && (
          <CreateVendorDialog
            open
            initialName={vendorCreation.initialName}
            onCancel={cancelVendorCreation}
            onSave={saveVendorCreation}
          />
        )}
        {customerCreation && (
          <CreateCustomerDialog
            open
            initialName={customerCreation.initialName}
            onCancel={cancelCustomerCreation}
            onSave={saveCustomerCreation}
          />
        )}
      </div>
    </HeaderFilterContext.Provider>
  );
};

export default InvoiceTable;
